import {
  useState,
  MouseEvent,
  useCallback,
  useMemo,
} from 'react';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import {
  Box,
  IconButton,
  Typography,
  Popover,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faStar } from '@fortawesome/pro-solid-svg-icons';
// import { faBell, faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';

import {
  appHeaderSelector,
  colorThemeSelector,
  timeZoneSelector,
  languageSelector,
} from 'store/app/appSelectors';
import { useTypedDispatch } from 'hooks/useTypedDispatch';
import { setLanguage, setTimeZone } from 'store/app/appReducer';
import { ColorTheme } from 'enum/ColorTheme';
import { supportedLanguages } from 'constants/languages';
import Breadcrumb, { IBreadCrumbItem } from './components/Breadcrumb/Breadcrumb';
import AccountMenu from './components/AccountMenu/AccountMenu';
import NavigationMobilePane from '../NavigationPane/NavigationMobilePane';
import BurgerMenu from './components/BurgerMenu/BurgerMenu';
import { MAXRATING } from '../../layouts/PlugIns/AddPlugInsPage/PlugInCard/PlugInCard';
import AppTimeZoneSelect from '../AppTimeZoneSelect/AppTimeZoneSelect';
import AppSelect from '../AppSelect/AppSelect';

import { ReactComponent as Logo } from '../../../assets/logo.svg';

import './AppHeader.scss';

export interface IAppHeader {
  title: string;
  menuItems?: IBreadCrumbItem[];
  hideNavToPev?: boolean;
  description?: string;
  rating?: number;
  hideTitle?: boolean;
}

// TODO: remove when pages description is provided
const HIDE_DESCRIPTION = true;

const AppHeader = () => {
  const dispatch = useTypedDispatch();

  const theme = useSelector(colorThemeSelector);
  const timeZone = useSelector(timeZoneSelector);
  const language = useSelector(languageSelector);
  const data = useSelector(appHeaderSelector);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const open = Boolean(anchorEl);

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const [openMenu, setOpenMenu] = useState(false);

  const changeMenuActivity = useCallback(() => {
    setOpenMenu(!openMenu);
  }, [openMenu, setOpenMenu]);

  const languageSelect = useMemo(() => (
    <AppSelect
      options={supportedLanguages}
      initialValue={language}
      formatValue={(value) => value.toUpperCase()}
      onChangeValue={(value) => dispatch(setLanguage(value))}
      className="language-select"
      theme={ColorTheme.RED}
    />
  ), [language]);

  const timezoneSelect = useMemo(() => (
    <AppTimeZoneSelect
      disableClearable
      selectedOption={timeZone}
      onChange={(value) => {
        if (value !== timeZone) dispatch(setTimeZone(value));
      }}
      className="header__header-timezone"
      theme={ColorTheme.RED}
    />
  ), [timeZone]);

  return (
    <Box className={`header ${theme}`}>
      <Box className="mobile-header">
        <Box className="mobile-header_wrapper">
          <Logo />
          <BurgerMenu open={openMenu} onClose={changeMenuActivity} />
        </Box>
        <Box className={`mobile-header_menu ${openMenu ? 'open' : 'close'}`}>
          <NavigationMobilePane />
          <Box className="header__header-buttons account-mobile-menu">
            <Box className="timezone-wrapper">
              {timezoneSelect}
              {languageSelect}
            </Box>
            {/* <Box>
              <IconButton aria-label="notifications" className="header__header-buttons-item">
                <FontAwesomeIcon icon={faBell} />
              </IconButton>
              <span>{t('links.notifications')}</span>
            </Box>
            <Box>
              <IconButton aria-label="support" className="header__header-buttons-item">
                <FontAwesomeIcon icon={faCircleQuestion} />
              </IconButton>
              <span>{t('links.support')}</span>
            </Box> */}
            <AccountMenu />
          </Box>
        </Box>
      </Box>
      <Box className="header__header">
        {!!data?.menuItems
        && <Breadcrumb items={data.menuItems} hideNavToPev={data.hideNavToPev} />}
        {timezoneSelect}
        {languageSelect}
        <Box className="header__header-buttons">
          {/* <IconButton aria-label="notifications" className="header__header-buttons-item">
            <FontAwesomeIcon icon={faBell} />
          </IconButton>
          <IconButton aria-label="support" className="header__header-buttons-item">
            <FontAwesomeIcon icon={faCircleQuestion} />
          </IconButton> */}
          <AccountMenu />
        </Box>
      </Box>
      <Box className={classNames('header__wrapper', { 'is-open': !data.hideTitle })}>
        <Box className="header__main">
          <Typography component="h1" data-testid="main-header">
            {data.title}
          </Typography>
          {!HIDE_DESCRIPTION && data.description && (
            <>
              <IconButton onClick={handleClick}>
                <FontAwesomeIcon icon={faCircleInfo} className="info" />
              </IconButton>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
              >
                <Typography sx={{ p: 2 }}>{data.description}</Typography>
              </Popover>
            </>
          )}
          {data.rating && (
            <span className="plug-in-rating">
              <FontAwesomeIcon icon={faStar} />
              {`${data.rating} / ${MAXRATING}`}
            </span>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppHeader;
